import React, { useMemo } from "react";
import { McTable, McButton } from "@maersk-global/mds-react-wrapper";
import { PLATFORM_COLUMNS, PORTFOLIO_COLUMNS } from "./constants";
import { transformPlatformAnalytics, transformPortfolioAnalytics } from "./utils";

export default function AnalyticsSection({
  redGreenAnalytics,
  loading,
  onDownloadPlatform,
  onDownloadPortfolio,
  renderPlatformColumnFilter,
  renderPortfolioColumnFilter,
  platformColumnFilters,
  portfolioColumnFilters,
  applyColumnFilters,
}) {
  const platformAnalyticsDataRaw = transformPlatformAnalytics(redGreenAnalytics);
  const portfolioAnalyticsDataRaw = transformPortfolioAnalytics(redGreenAnalytics);
  
  // Apply filters to the data
  const platformAnalyticsData = useMemo(
    () => applyColumnFilters(platformAnalyticsDataRaw, platformColumnFilters),
    [platformAnalyticsDataRaw, platformColumnFilters, applyColumnFilters]
  );
  
  const portfolioAnalyticsData = useMemo(
    () => applyColumnFilters(portfolioAnalyticsDataRaw, portfolioColumnFilters),
    [portfolioAnalyticsDataRaw, portfolioColumnFilters, applyColumnFilters]
  );

  return (
    <div style={{ marginBottom: "50px" }}>
      {/* Analytics Header */}
      <div
        style={{
          backgroundColor: "white",
          paddingTop: "20px",
   
          borderRadius: "8px 8px 0 0",
          color: "#333",
        }}
      >
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: "600",
            color: "#333",
            margin: 0,
          }}
        >
          Analytics
        </h2>
      </div>

      {/* Analytics Cards */}
      <div
        style={{
          backgroundColor: "white",
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          gap: "20px",
          paddingBottom: "20px",
          paddingTop: "20px",
        }}
      >
        {/* Total App IDs Card */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            maxHeight: "85px",
          }}
        >
          <div
            style={{
              fontSize: "0.875rem",
              color: "#666",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Total App IDs
          </div>
          <div
            style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#333",
            }}
          >
            {redGreenAnalytics?.unique_app_ids || 0}
          </div>
        </div>
        {/* Total App Instances Card */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            maxHeight: "85px",
          }}
        >
          <div
            style={{
              fontSize: "0.875rem",
              color: "#666",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Total App Instances
          </div>
          <div
            style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#333",
            }}
          >
            {redGreenAnalytics?.total_apps || 0}
          </div>
        </div>

        {/* Red Channel Card */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            maxHeight: "85px",
          }}
        >
          <div
            style={{
              fontSize: "0.875rem",
              color: "#666",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Red Channel (App Instances)
          </div>
          <div
            style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#e75858",
            }}
          >
            {redGreenAnalytics?.total_red || 0}
          </div>
        </div>

        {/* Green Channel Card */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            maxHeight: "85px",
          }}
        >
          <div
            style={{
              fontSize: "0.875rem",
              color: "#666",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Green Channel (App Instances)
          </div>
          <div
            style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#00786b",
            }}
          >
            {redGreenAnalytics?.total_green || 0}
          </div>
        </div>

        
      </div>

      {/* Platform and Portfolio Analytics Tables */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "30px",
          padding: "30px",
          backgroundColor: "white",
          borderRadius: "8px 8px 8px 8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {/* Portfolio Analytics */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <h3
              style={{
                fontSize: "1.2rem",
                fontWeight: "600",
                color: "#333",
                margin: 0,
              }}
            >
              Portfolio Analytics
            </h3>
            <McButton
              label="Download"
              appearance="primary"
              variant="filled"
              fit="small"
              click={onDownloadPortfolio}
              disabled={loading || portfolioAnalyticsData.length === 0}
            >
              <span slot="trailingicon">
                <mc-icon icon="tray-arrow-down" color="white"></mc-icon>
              </span>
            </McButton>
          </div>
          <McTable
            data={portfolioAnalyticsData}
            columns={PORTFOLIO_COLUMNS}
            datakey="id"
            headersticky
            fit="small"
            customstyles=".mds-table { max-height: 400px !important; }"
            sortdefaultcolumnid="red_channel"
            sortdefaultdirection="descending"
          >
            {loading ? (
              <mc-loading-indicator slot="state"></mc-loading-indicator>
            ) : (
              portfolioAnalyticsData?.length === 0 && (
                <div
                  slot="state"
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  No Data Available
                </div>
              )
            )}
            {/* Portfolio column filter */}
            {renderPortfolioColumnFilter && renderPortfolioColumnFilter('portfolio', 'Portfolio', 'portfolio', portfolioAnalyticsDataRaw)}
            {portfolioAnalyticsData?.map((row) => (
              <React.Fragment key={row.id}>
                <div slot={`${row.id}_red_channel`}>
                  <mc-tag appearance="error" fit="small">
                    {row.red_channel}
                  </mc-tag>
                </div>
                <div slot={`${row.id}_green_channel`}>
                  <mc-tag appearance="success" fit="small">
                    {row.green_channel}
                  </mc-tag>
                </div>
                <div slot={`${row.id}_red_percentage`}>
                  <span
                    style={{
                      color: row.red_percentage > 0 ? "#e75858" : "#666",
                      fontWeight: "600",
                    }}
                  >
                    {row.red_percentage}%
                  </span>
                </div>
              </React.Fragment>
            ))}
          </McTable>
        </div>

        {/* Platform Analytics */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <h3
              style={{
                fontSize: "1.2rem",
                fontWeight: "600",
                color: "#333",
                margin: 0,
              }}
            >
              Platform Analytics
            </h3>
            <McButton
              label="Download"
              appearance="primary"
              variant="filled"
              fit="small"
              click={onDownloadPlatform}
              disabled={loading || platformAnalyticsData.length === 0}
            >
              <span slot="trailingicon">
                <mc-icon icon="tray-arrow-down" color="white"></mc-icon>
              </span>
            </McButton>
          </div>
          <McTable
            data={platformAnalyticsData}
            columns={PLATFORM_COLUMNS}
            datakey="id"
            headersticky
            fit="small"
            customstyles=".mds-table { max-height: 400px !important; }"
            sortdefaultcolumnid="red_channel"
            sortdefaultdirection="descending"
          >
            {loading ? (
              <mc-loading-indicator slot="state"></mc-loading-indicator>
            ) : (
              platformAnalyticsData?.length === 0 && (
                <div
                  slot="state"
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  No Data Available
                </div>
              )
            )}
            {/* Platform column filter */}
            {renderPlatformColumnFilter && renderPlatformColumnFilter('platform', 'Platform', 'platform', platformAnalyticsDataRaw)}
            {platformAnalyticsData?.map((row) => (
              <React.Fragment key={row.id}>
                <div slot={`${row.id}_red_channel`}>
                  <mc-tag appearance="error" fit="small">
                    {row.red_channel}
                  </mc-tag>
                </div>
                <div slot={`${row.id}_green_channel`}>
                  <mc-tag appearance="success" fit="small">
                    {row.green_channel}
                  </mc-tag>
                </div>
                <div slot={`${row.id}_red_percentage`}>
                  <span
                    style={{
                      color: row.red_percentage > 0 ? "#e75858" : "#666",
                      fontWeight: "600",
                    }}
                  >
                    {row.red_percentage}%
                  </span>
                </div>
              </React.Fragment>
            ))}
          </McTable>
        </div>

        
      </div>
    </div>
  );
}
